import { useEffect } from "react";
import PhoneInput from "react-phone-number-input";
import useDefaultCountry from "../lib/useDefaultCountry";
import { isValidPhoneNumber } from "../lib/phone";
import "react-phone-number-input/style.css";

/**
 * PhoneInputE164 — single component used by every public lead-capture form.
 *
 * Wraps `react-phone-number-input` (which bundles libphonenumber-js + country
 * flags + a searchable country dropdown) and presents a Tailwind-styled
 * surface that visually matches the surrounding form controls.
 *
 * Behaviour:
 *  - Auto-detects the user's country on first render (IP-based) but lets the
 *    user override via the country dropdown beside the input.
 *  - Always emits a value in E.164 format (e.g. "+61493800777") so callers can
 *    persist a single normalised string in the DB.
 *  - When the page provides a `preferCountry` (e.g. "AU" on an Australia hub
 *    page) it skips IP detection and uses that as the default.
 *  - Validity (`isValidPhoneNumber`) is exposed so the parent form can disable
 *    submit when the typed number is incomplete or wrong for the chosen
 *    country.
 *
 * NOTE: This component is intentionally controlled (`value` + `onChange`) —
 * we do NOT keep our own internal state because the legacy form components
 * already manage their state and we want a one-line drop-in replacement.
 */
export default function PhoneInputE164({
    value,
    onChange,
    preferCountry,            // optional ISO-2, e.g. "AU"
    forceCountry,             // V44 — when supplied, forces the country picker
                              // to this ISO-2 (used by the Foreign/NRI demo
                              // form to sync flag with the Country combobox).
    onCountryChange,          // V44 — callback fires when the user manually
                              // changes the flag/code from the country picker.
    required = false,
    disabled = false,
    placeholder = "Mobile number",
    testid = "phone-input-e164",
    className = "",
}) {
    const [defaultCountry, setDefaultCountry] = useDefaultCountry(preferCountry);

    // V44 — when the parent passes `forceCountry` (e.g. user picked "Canada"
    // in the Country combobox), align the dropdown flag immediately so the
    // visible flag matches the explicit country selection.
    useEffect(() => {
        if (forceCountry && forceCountry !== defaultCountry) {
            setDefaultCountry(forceCountry);
        }
    }, [forceCountry]); // eslint-disable-line react-hooks/exhaustive-deps

    // When the user hasn't typed anything yet AND the detected country
    // changes (e.g. after the IP probe resolves), re-render with the new
    // default. PhoneInput is uncontrolled w.r.t. country until value has a
    // country prefix, so this just nudges the dropdown to show the right flag.
    useEffect(() => {
        // no-op effect — bound so changes to `defaultCountry` re-render
    }, [defaultCountry]);

    const invalid = !!value && !isValidPhoneNumber(value);

    return (
        <div className={`phone-input-e164 ${className}`} data-testid={testid} data-valid={value ? (invalid ? "false" : "true") : "empty"}>
            <PhoneInput
                international
                countryCallingCodeEditable={false}
                defaultCountry={defaultCountry}
                value={value || ""}
                onChange={(v) => onChange(v || "")}
                onCountryChange={(iso) => {
                    if (iso) setDefaultCountry(iso);
                    if (iso && typeof onCountryChange === "function") onCountryChange(iso);
                }}
                required={required}
                disabled={disabled}
                placeholder={placeholder}
                numberInputProps={{
                    "data-testid": `${testid}-number`,
                    className:
                        "PhoneInputInput w-full px-3 py-2.5 rounded-r-lg border border-l-0 border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent transition",
                }}
                countrySelectProps={{
                    "data-testid": `${testid}-country`,
                    "aria-label": "Country code",
                }}
            />
            {invalid && (
                <p className="text-[11px] text-red-600 mt-1" data-testid={`${testid}-error`}>
                    Enter a valid phone number for the selected country.
                </p>
            )}
        </div>
    );
}
